// Racket Lab R4.1-A — Player / Technician Context Card.
//
// Renders a sensitive-safe projection of a user referenced from a service
// ticket. Reusable for both player and technician contexts — the role
// label + icon are passed in. Privacy:
//   • email is ONLY shown when the backend chose to include it (admin or
//     self). The component never *requests* it — if the key is absent, the
//     row simply does not render.
//   • completion_pct is shown as a thin progress bar (0..100) and skipped
//     when null (inactive / missing user).
//   • An inactive user surfaces a rose-toned "Inactive user" badge instead
//     of the full card body — no ghost data leak.

import React from "react";
import { User, Wrench, ExternalLink } from "lucide-react";

const _truncId = (id) => {
  if (!id) return "";
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
};

export default function ContextCard({
  role,            // "player" | "technician"
  context,         // backend projection or null
  t,
  isAdmin = false, // controls the "View →" link visibility
}) {
  if (!context) return null;

  const Icon = role === "technician" ? Wrench : User;
  const titleKey = role === "technician"
    ? "racketLab.tickets.contextTechnicianTitle"
    : "racketLab.tickets.contextPlayerTitle";

  const inactive = context.is_active === false;
  const testid = `racket-lab-tickets-detail-context-${role}`;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${role === "technician" ? "text-amber-600" : "text-sky-600"}`} />
          <span className="text-sm font-semibold text-slate-900">{t(titleKey)}</span>
        </div>
        {isAdmin && context.id && !inactive && (
          <a
            href={`/admin/users?q=${encodeURIComponent(context.email || context.id)}`}
            className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
            data-testid={`${testid}-view-link`}
          >
            {t("racketLab.tickets.contextViewLink")} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {inactive ? (
        <div
          className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1"
          data-testid={`${testid}-inactive`}
        >
          {t("racketLab.tickets.contextInactive")} · <span className="font-mono">{_truncId(context.id)}</span>
        </div>
      ) : (
        <>
          <div className="text-sm font-medium text-slate-900 truncate">
            {context.full_name}
          </div>
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
            {context.profile_key && (
              <span data-testid={`${testid}-profile-key`}>{context.profile_key}</span>
            )}
            {context.country && <span>· {context.country}</span>}
            {context.language && <span>· {context.language}</span>}
            <span className="font-mono text-slate-400">· {_truncId(context.id)}</span>
            {context.email && (
              <span
                className="text-slate-600"
                data-testid={`${testid}-email`}
              >
                · {context.email}
              </span>
            )}
          </div>

          {typeof context.completion_pct === "number" && (
            <div className="mt-3" data-testid={`${testid}-completion`}>
              <div className="flex items-center justify-between text-[10px] text-slate-500 mb-0.5">
                <span>{t("racketLab.tickets.contextCompletion")}</span>
                <span className="font-mono">{context.completion_pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    context.completion_pct >= 75
                      ? "bg-emerald-500"
                      : context.completion_pct >= 40
                      ? "bg-amber-500"
                      : "bg-rose-400"
                  }`}
                  style={{ width: `${Math.min(100, Math.max(0, context.completion_pct))}%` }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
